// frontend/src/components/SettingsPage.tsx
//
// Settings (Dropbox Dash / Claude pattern): a sticky section nav on wide
// screens plus a narrow column of sections. Every row is label + description
// on the left and the control on the right. There is no separate Save step.
//
// Account (balance, risk per trade, the daily limits) is the desk's server
// config (/api/day-risk/config), so the ticket, the desk header, the safety
// limit, the Telegram scanner and the engine's practice trades all size from
// the same numbers. The other sections persist to localStorage via useSettings.
import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import { signOut, useAuth } from "@/lib/auth";
import type { Key } from "react-aria-components";
import {
  Alert,
  Button,
  Chip,
  Drawer,
  Label,
  NumberField,
  Separator,
  Switch,
  ToggleButton,
  ToggleButtonGroup,
} from "@heroui/react";
import { Check, LogOut } from "lucide-react";
import { cn } from "@/lib/utils";
import { useSettings, type Settings } from "../hooks/useSettings";
import { DAY_RISK_CHANGED } from "../hooks/useDayRisk";
import type { DailyRiskConfig } from "./daytrader/dayTrader";
import { UniverseInfo } from "./desk/UniverseInfo";
import { volumeText, type Universe } from "./desk/deskData";

interface DrawdownStatus {
  paused: boolean;
  drawdownPct: number;
  tier: string;
}

/** GET /api/engine/config: the Against-BTC switch and the engine's fixed rules. */
interface EngineConfig {
  againstBtc: boolean;
  againstBtcRisk: number;
  minScore: number;
  minRR: number;
  maxCostR: number;
  minQuality: string[];
}

const RISK_PRESETS = [0.5, 1, 2] as const;

const SECTIONS = [
  { id: "account", label: "Account" },
  { id: "coins", label: "Day-trading coins" },
  { id: "filters", label: "Signal rules" },
  { id: "risk", label: "Risk management" },
] as const;

export function SettingsPage() {
  const { settings, updateSettings } = useSettings();
  const auth = useAuth();
  // Account size, risk per trade and the daily limits live on the server
  // (day_risk_config): the desk, the ticket, the safety limit and the Telegram
  // scanner all read these same numbers.
  const [day, setDay] = useState<DailyRiskConfig | null>(null);
  const [dayError, setDayError] = useState<string | null>(null);
  const [customRiskInput, setCustomRiskInput] = useState("");
  const [showCustomInput, setShowCustomInput] = useState(false);
  useEffect(() => {
    let cancelled = false;
    fetch("/api/day-risk/config")
      .then((r) => r.json())
      .then((j) => {
        if (cancelled) return;
        if (!j.success) { setDayError(j.error || "Couldn't load the account settings"); return; }
        const cfg = j.config as DailyRiskConfig;
        setDay(cfg);
        setCustomRiskInput(String(cfg.riskPerTradePct));
        setShowCustomInput(!RISK_PRESETS.includes(cfg.riskPerTradePct as typeof RISK_PRESETS[number]));
      })
      .catch(() => { if (!cancelled) setDayError("Couldn't load the account settings — is the backend running?"); });
    return () => { cancelled = true; };
  }, []);
  // The engine's signal rules (/api/engine/config): one switch, the rest fixed.
  const [engine, setEngine] = useState<EngineConfig | null>(null);
  const [engineError, setEngineError] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    fetch("/api/engine/config")
      .then((r) => r.json())
      .then((j) => {
        if (cancelled) return;
        if (j.success) setEngine(j.config as EngineConfig);
        else setEngineError(j.error || "Couldn't load the signal rules");
      })
      .catch(() => { if (!cancelled) setEngineError("Couldn't load the signal rules — is the backend running?"); });
    return () => { cancelled = true; };
  }, []);
  const [drawdownStatus, setDrawdownStatus] = useState<DrawdownStatus | null>(null);
  const [overriding, setOverriding] = useState(false);
  const [overrideToast, setOverrideToast] = useState<string | null>(null);
  const [justSaved, setJustSaved] = useState(false);
  // Today's coin list (picked daily by the backend; read-only here).
  const [universe, setUniverse] = useState<Universe | null>(null);
  const [showUniverse, setShowUniverse] = useState(false);
  useEffect(() => {
    let cancelled = false;
    fetch("/api/trading-signals/universe")
      .then(r => r.json())
      .then(j => { if (!cancelled && j.success) setUniverse(j.universe); })
      .catch(() => { /* the list just stays "loading" */ });
    return () => { cancelled = true; };
  }, []);
  const savedTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const activeSection = useScrollSpy(SECTIONS.map((s) => s.id));

  useEffect(() => {
    fetch("/api/drawdown/status")
      .then((r) => r.json())
      .then((data) => {
        // API shape: { success, state: { drawdownPct, ... }, tier: { tier, multiplier, paused, message } }
        const state = data.state ?? {};
        const tier = data.tier ?? {};
        setDrawdownStatus({
          paused: tier.paused ?? false,
          drawdownPct: state.drawdownPct ?? 0,
          tier: tier.tier ?? "normal",
        });
      })
      .catch(() => {});
  }, []);

  useEffect(() => () => {
    if (savedTimer.current) clearTimeout(savedTimer.current);
  }, []);

  function flashSaved() {
    setJustSaved(true);
    if (savedTimer.current) clearTimeout(savedTimer.current);
    savedTimer.current = setTimeout(() => setJustSaved(false), 1500);
  }

  /** updateSettings (persists immediately) + a brief "Saved" confirmation. */
  function save(patch: Partial<Settings>) {
    updateSettings(patch);
    flashSaved();
  }

  /** PUT the Against-BTC switch to the engine (the scanner, desk and chart read it). */
  async function saveEngine(patch: { againstBtc: boolean }) {
    const before = engine;
    if (engine) setEngine({ ...engine, ...patch });
    try {
      const res = await fetch("/api/engine/config", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(patch),
      });
      const json = await res.json();
      if (!json.success) throw new Error(json.error || "Save failed");
      setEngine(json.config as EngineConfig);
      setEngineError(null);
      flashSaved();
    } catch (err) {
      setEngine(before);
      setEngineError(err instanceof Error && err.message !== "Failed to fetch" ? err.message : "Save failed — check the backend connection");
    }
  }

  /** PUT an account / daily-limit change to the desk; every open panel re-reads it. */
  async function saveDay(patch: Partial<DailyRiskConfig>) {
    try {
      const res = await fetch("/api/day-risk/config", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(patch),
      });
      const json = await res.json();
      if (!json.success) { setDayError(json.error || "Save failed"); return; }
      setDay(json.config as DailyRiskConfig);
      setDayError(null);
      window.dispatchEvent(new Event(DAY_RISK_CHANGED));
      flashSaved();
    } catch {
      setDayError("Save failed — check the backend connection");
    }
  }

  async function overrideDrawdown() {
    setOverriding(true);
    try {
      const res = await fetch("/api/drawdown/override", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ confirm: true }),
      });
      if (res.ok) {
        setDrawdownStatus((prev) => prev ? { ...prev, paused: false } : prev);
        setOverrideToast("Drawdown override applied");
      } else {
        setOverrideToast("Override failed — try again");
      }
    } catch {
      setOverrideToast("Override failed — check connection");
    } finally {
      setOverriding(false);
      setTimeout(() => setOverrideToast(null), 3000);
    }
  }

  const riskPct = day?.riskPerTradePct ?? RISK_PRESETS[0];
  const riskKey = showCustomInput ? "custom" : String(riskPct);

  return (
    <div className="mx-auto grid w-full max-w-5xl gap-8 lg:grid-cols-[180px_minmax(0,1fr)]">
      <Drawer.Backdrop isOpen={showUniverse} onOpenChange={setShowUniverse}>
        <Drawer.Content placement="right">
          <Drawer.Dialog className="w-full sm:w-[30rem]">
            <Drawer.CloseTrigger />
            <Drawer.Header><Drawer.Heading>Why these coins?</Drawer.Heading></Drawer.Header>
            <Drawer.Body>{universe && <UniverseInfo universe={universe} />}</Drawer.Body>
            <Drawer.Footer><Button slot="close" variant="secondary">Close</Button></Drawer.Footer>
          </Drawer.Dialog>
        </Drawer.Content>
      </Drawer.Backdrop>
      {/* ── Section nav (wide screens) ─────────────────────────────────── */}
      <nav aria-label="Settings sections" className="hidden lg:block">
        <ul className="sticky top-20 space-y-0.5">
          {SECTIONS.map((s) => (
            <li key={s.id}>
              <a
                href={`#${s.id}`}
                onClick={(e) => {
                  e.preventDefault();
                  document.getElementById(s.id)?.scrollIntoView({ behavior: "smooth", block: "start" });
                }}
                aria-current={activeSection === s.id ? "true" : undefined}
                className={cn(
                  "block rounded-lg px-3 py-1.5 text-[13px] outline-none transition-colors focus-visible:ring-2 focus-visible:ring-focus",
                  activeSection === s.id
                    ? "bg-default font-medium text-foreground"
                    : "text-muted hover:bg-default hover:text-foreground",
                )}
              >
                {s.label}
              </a>
            </li>
          ))}
        </ul>
      </nav>

      <div className="min-w-0 max-w-3xl space-y-10">
        {/* Save status — settings persist on every change */}
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-separator pb-4">
          <p className="text-[13px] text-muted">Changes save automatically. Account, daily limits and signal rules are kept on the desk; risk caps in this browser.</p>
          <Chip
            size="sm"
            variant="soft"
            color={justSaved ? "success" : "default"}
            aria-live="polite"
            className="gap-1"
          >
            <Check className="size-3" />
            {justSaved ? "Saved" : "All changes saved"}
          </Chip>
        </div>

        {/* ── Account ───────────────────────────────────────────────────── */}
        <Section id="account" title="Account" description="One set of numbers for the desk, the ticket, the safety limit and the Telegram scanner: your balance, a fixed risk per trade, and the daily limits.">
          {auth.username && (
            <Row label="Signed in" description={<>As <span className="font-medium text-foreground">{auth.username}</span>. You stay signed in on this device for a year, until you sign out.</>}>
              {() => (
                <Button size="sm" variant="secondary" onPress={() => void signOut()}>
                  <LogOut className="size-3.5" /> Sign out
                </Button>
              )}
            </Row>
          )}
          <Row label="Account size" description="Balance the desk sizes positions from.">
            {(ids) => (
              <NumberField
                aria-labelledby={ids.label}
                aria-describedby={ids.description}
                variant="secondary"
                minValue={1}
                value={day?.accountSize ?? 500}
                isDisabled={!day}
                formatOptions={{ style: "currency", currency: "USD", maximumFractionDigits: 2 }}
                onChange={(val) => {
                  if (val != null && val > 0) saveDay({ accountSize: val });
                }}
              >
                <NumberField.Group>
                  <NumberField.Input className="num w-32 text-right" />
                </NumberField.Group>
              </NumberField>
            )}
          </Row>
          <Separator />
          <Row
            label="Risk per trade"
            description="0.5% is the desk default. Stay at or below 1% while you're learning; never more than 2% on a single trade."
            stacked
          >
            {(ids) => (
              <div className="flex flex-wrap items-center gap-3">
                <ToggleButtonGroup
                  aria-labelledby={ids.label}
                  aria-describedby={ids.description}
                  selectionMode="single"
                  size="sm"
                  selectedKeys={new Set<Key>(RISK_PRESETS.some((p) => String(p) === riskKey) || riskKey === "custom" ? [riskKey] : [])}
                  // Selection is derived from settings; each button's onPress applies it.
                  onSelectionChange={() => {}}
                >
                  {RISK_PRESETS.map((p, i) => (
                    <ToggleButton
                      key={p}
                      id={String(p)}
                      className="num"
                      isDisabled={!day}
                      onPress={() => {
                        saveDay({ riskPerTradePct: p });
                        setCustomRiskInput(String(p));
                        setShowCustomInput(false);
                      }}
                    >
                      {i > 0 && <ToggleButtonGroup.Separator />}
                      {p}%
                    </ToggleButton>
                  ))}
                  <ToggleButton
                    id="custom"
                    onPress={() => {
                      setShowCustomInput(true);
                      setCustomRiskInput("");
                    }}
                  >
                    <ToggleButtonGroup.Separator />
                    Custom
                  </ToggleButton>
                </ToggleButtonGroup>
                {showCustomInput && (
                  <NumberField
                    aria-label="Custom risk per trade"
                    variant="secondary"
                    minValue={0.1}
                    maxValue={10}
                    step={0.1}
                    value={customRiskInput === "" ? Number.NaN : parseFloat(customRiskInput)}
                    onChange={(val) => {
                      // NumberField commits on blur / Enter — same moment the old input's onBlur fired.
                      setCustomRiskInput(val == null || Number.isNaN(val) ? "" : String(val));
                      if (val != null && val > 0) saveDay({ riskPerTradePct: val });
                    }}
                  >
                    <NumberField.Group className="items-center">
                      <NumberField.Input autoFocus placeholder="e.g. 1.5" className="num w-20 text-right" />
                      <Unit>%</Unit>
                    </NumberField.Group>
                  </NumberField>
                )}
              </div>
            )}
          </Row>
          <Separator />
          <Row label="Max trades a day" description="Real trades only; practice trades don't count. The Playbook says 3: a fourth trade in a day means you went looking for one.">
            {(ids) => (
              <NumberField
                aria-labelledby={ids.label}
                aria-describedby={ids.description}
                variant="secondary"
                minValue={1}
                maxValue={100}
                step={1}
                value={day?.maxTradesPerDay ?? 3}
                isDisabled={!day}
                onChange={(val) => {
                  if (val != null && Number.isInteger(val) && val >= 1 && val <= 100) saveDay({ maxTradesPerDay: val });
                }}
              >
                <NumberField.Group>
                  <NumberField.Input className="num w-16 text-right" />
                </NumberField.Group>
              </NumberField>
            )}
          </Row>
          <Separator />
          <Row label="Max loss a day" description="Once today's losses plus open risk reach this, the desk shows LOCKED until 00:00 UTC. 3R is three full stop-outs.">
            {(ids) => (
              <NumberField
                aria-labelledby={ids.label}
                aria-describedby={ids.description}
                variant="secondary"
                minValue={0.5}
                maxValue={50}
                step={0.5}
                value={day?.maxDailyLossR ?? 3}
                isDisabled={!day}
                onChange={(val) => {
                  if (val != null && val > 0 && val <= 50) saveDay({ maxDailyLossR: val });
                }}
              >
                <NumberField.Group className="items-center">
                  <NumberField.Input className="num w-16 text-right" />
                  <Unit>R</Unit>
                </NumberField.Group>
              </NumberField>
            )}
          </Row>
          {dayError && <p className="py-3 text-xs text-loss" role="alert">{dayError}</p>}
        </Section>

        {/* ── Day-trading coins ───────────────────────────────────────────── */}
        <Section
          id="coins"
          title="Day-trading coins"
          description="No watchlist to manage: the coins are picked for you every day at 00:00 UTC. The desk and the Telegram scanner use exactly this list."
        >
          <Row
            label="Today's coins"
            description="The busiest coins (at least $150M traded in 24 hours) that move enough to pay for fees (at least 0.6% an hour), with over a year of history and no pump pattern. Busiest first."
            stacked
          >
            {() => (
              <div className="space-y-2">
                <div className="flex flex-wrap gap-1.5">
                  {universe
                    ? universe.included.map((c) => (
                        <Chip key={c.symbol} size="sm" variant="secondary" className="num" title={c.why}>
                          {c.symbol.replace("USDT", "")}
                          <span className="ml-1 text-muted">{volumeText(c.volumeM)}</span>
                        </Chip>
                      ))
                    : <span className="text-xs text-muted">Loading today's list…</span>}
                </div>
                {universe && (
                  <Button size="sm" variant="ghost" className="-ml-2" onPress={() => setShowUniverse(true)}>
                    Why these coins?
                  </Button>
                )}
              </div>
            )}
          </Row>
        </Section>

        {/* ── Signal rules ──────────────────────────────────────────────── */}
        <Section
          id="filters"
          title="Signal rules"
          description="What the engine checks before it sends a signal: the desk, Telegram and the chart all use these."
        >
          <Row
            label="Against-BTC signals"
            description={
              engine
                ? `On: a signal against a strong BTC trend still goes out, at ${engine.againstBtcRisk === 0.5 ? "half" : `${engine.againstBtcRisk * 100}% of`} your usual risk, labelled "Against BTC". Off: it is blocked, the old BTC filter.`
                : "Loading the engine's settings…"
            }
          >
            {(ids) => (
              <Toggle
                labelledBy={ids.label}
                describedBy={ids.description}
                value={engine?.againstBtc ?? true}
                isDisabled={!engine}
                onChange={(v) => saveEngine({ againstBtc: v })}
              />
            )}
          </Row>
          {engineError && <p className="pb-3 text-xs text-loss">{engineError}</p>}
          <Separator />
          <FixedRule label="Minimum score" value={engine ? `${engine.minScore} of 100` : "…"} description="How strongly the indicators must agree on one direction." />
          <Separator />
          <FixedRule label="Reward vs risk" value={engine ? `at least ${engine.minRR}×` : "…"} description="Target 1 must pay at least this many times the risk to the stop." />
          <Separator />
          <FixedRule label="Fees" value={engine ? `under ${engine.maxCostR}R` : "…"} description="Fees and slippage must cost less than this share of the risk, so the stop can't be too tight." />
          <Separator />
          <FixedRule label="Setup grade" value={engine ? engine.minQuality.join(", ") : "…"} description="Weaker setups are never sent." />
          <Separator />
          <FixedRule label="Macro mood" value="Information only" description="Shown in the desk header and on Telegram. The engine doesn't block signals with it." />
          <p className="py-3 text-xs text-muted">
            The fixed rules change only when a backtest on months of data shows a better value (Playbook: "Change the rules only with data").
          </p>
        </Section>

        {/* ── Risk management ───────────────────────────────────────────── */}
        <Section id="risk" title="Risk management" description="Hard caps on total exposure, and the drawdown brake.">
          <Row label="Portfolio heat cap" description="Maximum total risk across all open trades.">
            {(ids) => (
              <NumberField
                aria-labelledby={ids.label}
                aria-describedby={ids.description}
                variant="secondary"
                minValue={1}
                maxValue={20}
                step={0.5}
                value={settings.heatCap}
                onChange={(val) => {
                  if (val != null && val >= 1 && val <= 20) save({ heatCap: val });
                }}
              >
                <NumberField.Group className="items-center">
                  <NumberField.Input className="num w-16 text-right" />
                  <Unit>%</Unit>
                </NumberField.Group>
              </NumberField>
            )}
          </Row>
          <Separator />
          <Row label="Group exposure cap" description="Maximum risk per correlated group.">
            {(ids) => (
              <NumberField
                aria-labelledby={ids.label}
                aria-describedby={ids.description}
                variant="secondary"
                minValue={1}
                maxValue={10}
                step={0.5}
                value={settings.groupCap}
                onChange={(val) => {
                  if (val != null && val >= 1 && val <= 10) save({ groupCap: val });
                }}
              >
                <NumberField.Group className="items-center">
                  <NumberField.Input className="num w-16 text-right" />
                  <Unit>%</Unit>
                </NumberField.Group>
              </NumberField>
            )}
          </Row>

          {drawdownStatus && (
            <>
              <Separator />
              <Row
                label="Drawdown override"
                description={
                  <>
                    Current:{" "}
                    <span className="num font-semibold text-foreground">{drawdownStatus.drawdownPct.toFixed(1)}%</span>{" "}
                    <span className="capitalize">({drawdownStatus.tier})</span>
                  </>
                }
              >
                {() =>
                  drawdownStatus.paused ? (
                    <Button size="sm" variant="danger" isPending={overriding} onPress={overrideDrawdown}>
                      {overriding ? "Overriding..." : "Override Pause"}
                    </Button>
                  ) : (
                    <Chip size="sm" variant="soft" className="bg-profit-soft font-semibold text-profit">
                      Trading active
                    </Chip>
                  )
                }
              </Row>
              {drawdownStatus.paused && !overrideToast && (
                <Alert status="warning" className="mt-3">
                  <Alert.Indicator />
                  <Alert.Content>
                    <Alert.Title>Trading is paused by the drawdown brake</Alert.Title>
                    <Alert.Description>
                      Overriding resumes signals before the account has recovered. Only do it if you know why the brake tripped.
                    </Alert.Description>
                  </Alert.Content>
                </Alert>
              )}
              {overrideToast && (
                <Alert
                  status={overrideToast === "Drawdown override applied" ? "success" : "danger"}
                  className="mt-3"
                  role="status"
                >
                  <Alert.Indicator />
                  <Alert.Content>
                    <Alert.Title>{overrideToast}</Alert.Title>
                  </Alert.Content>
                </Alert>
              )}
            </>
          )}
        </Section>
      </div>
    </div>
  );
}

// ─── layout bits ─────────────────────────────────────────────────────────────

function Section({ id, title, description, children }: { id: string; title: string; description: string; children: ReactNode }) {
  return (
    <section id={id} aria-labelledby={`${id}-heading`} className="scroll-mt-20">
      <h2 id={`${id}-heading`} className="text-sm font-semibold text-foreground">{title}</h2>
      <p className="mt-0.5 text-[13px] text-muted">{description}</p>
      <div className="mt-4 rounded-lg border border-border bg-surface px-4">{children}</div>
    </section>
  );
}

interface RowIds {
  label: string;
  description: string;
}

/**
 * One settings row: label + description on the left, control on the right.
 * `stacked` puts the control under the text (wide controls: sliders, tag lists).
 * The control receives the label/description ids for aria-labelledby/-describedby.
 */
function Row({
  label,
  description,
  stacked,
  children,
}: {
  label: string;
  description?: ReactNode;
  stacked?: boolean;
  children: (ids: RowIds) => ReactNode;
}) {
  const uid = useId();
  const ids = { label: `${uid}-label`, description: `${uid}-desc` };
  return (
    <div
      className={cn(
        "py-4",
        stacked ? "space-y-3" : "flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-6",
      )}
    >
      <div className="min-w-0">
        <Label id={ids.label} className="text-[13px] font-medium text-foreground">{label}</Label>
        {description && (
          <p id={ids.description} className="mt-0.5 text-xs text-muted">{description}</p>
        )}
      </div>
      <div className={cn(!stacked && "shrink-0")}>{children(ids)}</div>
    </div>
  );
}

function Unit({ children }: { children: ReactNode }) {
  return <span aria-hidden className="num select-none pr-3 text-xs text-muted">{children}</span>;
}

function Toggle({
  value,
  onChange,
  labelledBy,
  describedBy,
  isDisabled,
}: {
  value: boolean;
  onChange: (v: boolean) => void;
  labelledBy: string;
  describedBy: string;
  isDisabled?: boolean;
}) {
  // The control must sit inside Switch.Content (HeroUI 3.2: the clickable
  // part). Without it the switch renders but nothing can press it.
  return (
    <Switch isSelected={value} onChange={onChange} isDisabled={isDisabled} aria-labelledby={labelledBy} aria-describedby={describedBy}>
      <Switch.Content className="cursor-pointer">
        <Switch.Control>
          <Switch.Thumb />
        </Switch.Control>
      </Switch.Content>
    </Switch>
  );
}

/** A rule the engine applies as a fixed constant: shown, not edited. */
function FixedRule({ label, value, description }: { label: string; value: string; description: string }) {
  return (
    <div className="flex flex-col gap-1 py-4 sm:flex-row sm:items-center sm:justify-between sm:gap-6">
      <div className="min-w-0">
        <div className="text-[13px] font-medium text-foreground">{label}</div>
        <p className="mt-0.5 text-xs text-muted">{description}</p>
      </div>
      <span className="num shrink-0 text-sm text-foreground">{value}</span>
    </div>
  );
}

/** Which section is currently at the top of the viewport (for the sticky nav). */
function useScrollSpy(ids: readonly string[]) {
  const [active, setActive] = useState<string>(ids[0] ?? "");
  const key = ids.join("|");
  useEffect(() => {
    const els = key.split("|").map((id) => document.getElementById(id)).filter((el): el is HTMLElement => !!el);
    if (els.length === 0 || typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((e) => e.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) setActive(visible[0].target.id);
      },
      { rootMargin: "-72px 0px -60% 0px" },
    );
    els.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [key]);
  return active;
}
